export type AchievementId =
  | 'arena-season-top-10'
  | 'arena-season-number-1'
  | 'arena-no-regeneration-top-10'
  | 'arena-no-regeneration-number-1'
  | 'arena-first-run'
  | 'arena-regular'
  | 'arena-veteran'
  | 'duelist'
  | 'gladiator'
  | 'warlord'
  | 'banksitter'
  | 'moneybags'
  | 'pack-ripper'
  | 'pack-addict'
  | 'win-spree'
  | 'lady-luck'
  | 'cardholder'
  | 'flawless-victory'
  | 'deadeye'
  | 'slayer-blackthorn'
  | 'slayer-mondain'
  | 'slayer-minax'
  | 'slayer-exodus'
  | 'slayer-faulinei'
  | 'slayer-astaroth'
  | 'slayer-nosfentor';

export type TitleId =
  | 'arena-adept'
  | 'arena-grandmaster'
  | 'the-unmended'
  | 'the-unbroken'
  | 'first-blood'
  | 'arena-regular'
  | 'arena-veteran'
  | 'duelist'
  | 'gladiator'
  | 'warlord'
  | 'banksitter'
  | 'moneybags'
  | 'pack-ripper'
  | 'pack-addict'
  | 'win-spree'
  | 'lady-luck'
  | 'cardholder'
  | 'flawless-victory'
  | 'deadeye'
  | 'slayer-blackthorn'
  | 'slayer-mondain'
  | 'slayer-minax'
  | 'slayer-exodus'
  | 'slayer-faulinei'
  | 'slayer-astaroth'
  | 'slayer-nosfentor';

export interface LeaderboardKey {
  family: 'arena';
  partition: 'standard' | 'no-regeneration';
}

export interface LeaderboardRankCondition {
  kind: 'leaderboard-rank';
  board: LeaderboardKey;
  maximumRank: number;
}

/**
 * The named durable account counters a {@link CounterThresholdCondition} may
 * read (PRD #669). Each maps 1:1 onto an `AccountRecord` counter the server
 * writes at a single point: the Slice-1 four plus the Slice-2 trio
 * (`packsOpened` at the pack-open settlement, `duelWinStreak` at the duel
 * decided end, `diceWagerWins` at the dice-wager settle). Later waves add
 * more members without touching the evaluation machinery.
 */
export type AchievementCounter =
  | 'arenaRuns'
  | 'duelWins'
  | 'totalOnlineMs'
  | 'gold'
  | 'packsOpened'
  | 'duelWinStreak'
  | 'diceWagerWins';

/**
 * A pure counter-threshold milestone (PRD #669): satisfied the moment a named
 * durable counter reaches `minimum`. The declarative sibling of
 * {@link LeaderboardRankCondition} - matched by
 * {@link evaluateCounterAchievements}, persisted/deduped by the caller.
 */
export interface CounterThresholdCondition {
  kind: 'counter-threshold';
  counter: AchievementCounter;
  minimum: number;
}

/**
 * A one-shot achievement (PRD #669 Slice 3): no declarative matcher at all - the
 * server grants it directly via `grantAchievement` at the exact moment the
 * qualifying event resolves (the way the arena settle path already grants a
 * leaderboard achievement). The `kind` carries no payload; it exists so these
 * registry entries are honest, first-class data the totality guard still covers
 * (every achievement has an identically-named title), rather than titles wired up
 * from thin air. Neither {@link evaluateCounterAchievements} nor
 * {@link leaderboardAchievementsFor} ever returns a one-shot - both filter on
 * their own `kind` - so the pure matchers stay unaware of them by construction.
 * Today's one-shots: Cardholder (the starter-deck grant), Flawless Victory and
 * Deadeye (read from per-fight duel tallies at the fought-out decided end).
 */
export interface OneShotCondition {
  kind: 'one-shot';
}

/**
 * A per-run arena boss-kill milestone (PRD #669): satisfied when one arena run
 * finished with at least `minimumBossKills` bosses felled. Because the boss
 * rotation is deterministic (`ARENA_BOSSES[bossKills % 7]`, `src/sim/arena.ts`),
 * a run that reached N boss kills has necessarily defeated the first N villains
 * in rotation order - so `minimumBossKills` doubles as the identity of the boss
 * slain. Matched once at run settle by {@link arenaBossAchievementsFor}; never a
 * durable counter (the run itself is the evidence, no new wire/history field).
 */
export interface RunBossKillsCondition {
  kind: 'run-boss-kills';
  minimumBossKills: number;
}

export type AchievementCondition =
  | LeaderboardRankCondition
  | CounterThresholdCondition
  | OneShotCondition
  | RunBossKillsCondition;

export interface AchievementDefinition {
  id: AchievementId;
  name: string;
  description: string;
  condition: AchievementCondition;
  titleReward?: TitleId;
}

export interface TitleDefinition {
  id: TitleId;
  text: string;
  achievementId: AchievementId;
}

/** 100 hours of connected time, in the ms `totalOnlineMs` accrues. */
export const BANKSITTER_ONLINE_MS = 100 * 60 * 60 * 1000;
/** The balance-held-at-once Moneybags line. */
export const MONEYBAGS_GOLD = 100_000;

// The player-facing achievement name IS its title display text (PRD #669's
// one-canonical-string rule) - the totality test pins name === title.text for
// every entry, so the four launch arena achievements carry their title copy.
export const achievements: readonly AchievementDefinition[] = [
  {
    id: 'arena-season-top-10',
    name: 'Arena Adept',
    description: 'Reach rank 10 or better on the live Standard Arena board.',
    condition: {
      kind: 'leaderboard-rank',
      board: { family: 'arena', partition: 'standard' },
      maximumRank: 10,
    },
    titleReward: 'arena-adept',
  },
  {
    id: 'arena-season-number-1',
    name: 'Arena Grandmaster',
    description: 'Reach rank 1 on the live Standard Arena board.',
    condition: {
      kind: 'leaderboard-rank',
      board: { family: 'arena', partition: 'standard' },
      maximumRank: 1,
    },
    titleReward: 'arena-grandmaster',
  },
  {
    id: 'arena-no-regeneration-top-10',
    name: 'The Unmended',
    description: 'Reach rank 10 or better on the live No Regeneration Arena board.',
    condition: {
      kind: 'leaderboard-rank',
      board: { family: 'arena', partition: 'no-regeneration' },
      maximumRank: 10,
    },
    titleReward: 'the-unmended',
  },
  {
    id: 'arena-no-regeneration-number-1',
    name: 'The Unbroken',
    description: 'Reach rank 1 on the live No Regeneration Arena board.',
    condition: {
      kind: 'leaderboard-rank',
      board: { family: 'arena', partition: 'no-regeneration' },
      maximumRank: 1,
    },
    titleReward: 'the-unbroken',
  },
  {
    id: 'arena-first-run',
    name: 'First Blood',
    description: 'Complete your first arena run.',
    condition: { kind: 'counter-threshold', counter: 'arenaRuns', minimum: 1 },
    titleReward: 'first-blood',
  },
  {
    id: 'arena-regular',
    name: 'Arena Regular',
    description: 'Complete 10 arena runs.',
    condition: { kind: 'counter-threshold', counter: 'arenaRuns', minimum: 10 },
    titleReward: 'arena-regular',
  },
  {
    id: 'arena-veteran',
    name: 'Arena Veteran',
    description: 'Complete 50 arena runs.',
    condition: { kind: 'counter-threshold', counter: 'arenaRuns', minimum: 50 },
    titleReward: 'arena-veteran',
  },
  {
    id: 'duelist',
    name: 'Duelist',
    description: 'Win a melee duel.',
    condition: { kind: 'counter-threshold', counter: 'duelWins', minimum: 1 },
    titleReward: 'duelist',
  },
  {
    id: 'gladiator',
    name: 'Gladiator',
    description: 'Win 25 melee duels.',
    condition: { kind: 'counter-threshold', counter: 'duelWins', minimum: 25 },
    titleReward: 'gladiator',
  },
  {
    id: 'warlord',
    name: 'Warlord',
    description: 'Win 100 melee duels.',
    condition: { kind: 'counter-threshold', counter: 'duelWins', minimum: 100 },
    titleReward: 'warlord',
  },
  {
    id: 'banksitter',
    name: 'Banksitter',
    description: 'Accumulate 100 hours of online time at the bank.',
    condition: {
      kind: 'counter-threshold',
      counter: 'totalOnlineMs',
      minimum: BANKSITTER_ONLINE_MS,
    },
    titleReward: 'banksitter',
  },
  {
    id: 'moneybags',
    name: 'Moneybags',
    description: 'Hold 100,000 gold at once.',
    condition: { kind: 'counter-threshold', counter: 'gold', minimum: MONEYBAGS_GOLD },
    titleReward: 'moneybags',
  },
  {
    id: 'pack-ripper',
    name: 'Pack Ripper',
    description: 'Open 10 card packs.',
    condition: { kind: 'counter-threshold', counter: 'packsOpened', minimum: 10 },
    titleReward: 'pack-ripper',
  },
  {
    id: 'pack-addict',
    name: 'Pack Addict',
    description: 'Open 50 card packs.',
    condition: { kind: 'counter-threshold', counter: 'packsOpened', minimum: 50 },
    titleReward: 'pack-addict',
  },
  {
    id: 'win-spree',
    name: 'Win Spree',
    description: 'Win 5 melee duels in a row.',
    condition: { kind: 'counter-threshold', counter: 'duelWinStreak', minimum: 5 },
    titleReward: 'win-spree',
  },
  {
    id: 'lady-luck',
    name: 'Lady Luck',
    description: 'Win 10 dice wagers.',
    condition: { kind: 'counter-threshold', counter: 'diceWagerWins', minimum: 10 },
    titleReward: 'lady-luck',
  },
  {
    id: 'cardholder',
    name: 'Cardholder',
    description: 'Receive the starter deck on your first Card Binder open.',
    condition: { kind: 'one-shot' },
    titleReward: 'cardholder',
  },
  {
    id: 'flawless-victory',
    name: 'Flawless Victory',
    description: 'Win a melee duel without taking any damage.',
    condition: { kind: 'one-shot' },
    titleReward: 'flawless-victory',
  },
  {
    id: 'deadeye',
    name: 'Deadeye',
    description: 'Win a melee duel with bow shots only - no melee swings landed.',
    condition: { kind: 'one-shot' },
    titleReward: 'deadeye',
  },
  // The seven arena boss slayers (PRD #669 Slice 4), in ARENA_BOSSES rotation
  // order: minimumBossKills N == "reached the Nth boss in one run" == defeated
  // the (N)th villain, so the names track the roster (a drift test pins each
  // definition to ARENA_BOSSES[N-1]). Partition-agnostic - either board's runs
  // count. bossKills >= 8 is an escalated repeat lap that grants nothing new.
  {
    id: 'slayer-blackthorn',
    name: 'Slayer of Blackthorn',
    description: 'Defeat Lord Blackthorn in an arena run (1st boss).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 1 },
    titleReward: 'slayer-blackthorn',
  },
  {
    id: 'slayer-mondain',
    name: 'Slayer of Mondain',
    description: 'Defeat Mondain in an arena run (2nd boss).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 2 },
    titleReward: 'slayer-mondain',
  },
  {
    id: 'slayer-minax',
    name: 'Slayer of Minax',
    description: 'Defeat Minax in an arena run (3rd boss).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 3 },
    titleReward: 'slayer-minax',
  },
  {
    id: 'slayer-exodus',
    name: 'Slayer of Exodus',
    description: 'Defeat Exodus in an arena run (4th boss).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 4 },
    titleReward: 'slayer-exodus',
  },
  {
    id: 'slayer-faulinei',
    name: 'Slayer of Faulinei',
    description: 'Defeat Faulinei in an arena run (5th boss).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 5 },
    titleReward: 'slayer-faulinei',
  },
  {
    id: 'slayer-astaroth',
    name: 'Slayer of Astaroth',
    description: 'Defeat Astaroth in an arena run (6th boss).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 6 },
    titleReward: 'slayer-astaroth',
  },
  {
    id: 'slayer-nosfentor',
    name: 'Slayer of Nosfentor',
    description: 'Defeat Nosfentor in an arena run (7th boss - the full rotation).',
    condition: { kind: 'run-boss-kills', minimumBossKills: 7 },
    titleReward: 'slayer-nosfentor',
  },
] as const;

export const titles: readonly TitleDefinition[] = [
  { id: 'arena-adept', text: 'Arena Adept', achievementId: 'arena-season-top-10' },
  {
    id: 'arena-grandmaster',
    text: 'Arena Grandmaster',
    achievementId: 'arena-season-number-1',
  },
  {
    id: 'the-unmended',
    text: 'The Unmended',
    achievementId: 'arena-no-regeneration-top-10',
  },
  {
    id: 'the-unbroken',
    text: 'The Unbroken',
    achievementId: 'arena-no-regeneration-number-1',
  },
  { id: 'first-blood', text: 'First Blood', achievementId: 'arena-first-run' },
  { id: 'arena-regular', text: 'Arena Regular', achievementId: 'arena-regular' },
  { id: 'arena-veteran', text: 'Arena Veteran', achievementId: 'arena-veteran' },
  { id: 'duelist', text: 'Duelist', achievementId: 'duelist' },
  { id: 'gladiator', text: 'Gladiator', achievementId: 'gladiator' },
  { id: 'warlord', text: 'Warlord', achievementId: 'warlord' },
  { id: 'banksitter', text: 'Banksitter', achievementId: 'banksitter' },
  { id: 'moneybags', text: 'Moneybags', achievementId: 'moneybags' },
  { id: 'pack-ripper', text: 'Pack Ripper', achievementId: 'pack-ripper' },
  { id: 'pack-addict', text: 'Pack Addict', achievementId: 'pack-addict' },
  { id: 'win-spree', text: 'Win Spree', achievementId: 'win-spree' },
  { id: 'lady-luck', text: 'Lady Luck', achievementId: 'lady-luck' },
  { id: 'cardholder', text: 'Cardholder', achievementId: 'cardholder' },
  { id: 'flawless-victory', text: 'Flawless Victory', achievementId: 'flawless-victory' },
  { id: 'deadeye', text: 'Deadeye', achievementId: 'deadeye' },
  { id: 'slayer-blackthorn', text: 'Slayer of Blackthorn', achievementId: 'slayer-blackthorn' },
  { id: 'slayer-mondain', text: 'Slayer of Mondain', achievementId: 'slayer-mondain' },
  { id: 'slayer-minax', text: 'Slayer of Minax', achievementId: 'slayer-minax' },
  { id: 'slayer-exodus', text: 'Slayer of Exodus', achievementId: 'slayer-exodus' },
  { id: 'slayer-faulinei', text: 'Slayer of Faulinei', achievementId: 'slayer-faulinei' },
  { id: 'slayer-astaroth', text: 'Slayer of Astaroth', achievementId: 'slayer-astaroth' },
  { id: 'slayer-nosfentor', text: 'Slayer of Nosfentor', achievementId: 'slayer-nosfentor' },
] as const;

const achievementById = new Map(achievements.map((definition) => [definition.id, definition]));
const titleById = new Map(titles.map((definition) => [definition.id, definition]));

export function getAchievement(id: string): AchievementDefinition | undefined {
  return achievementById.get(id as AchievementId);
}

export function getTitle(id: string): TitleDefinition | undefined {
  return titleById.get(id as TitleId);
}

export interface LeaderboardRankEvent {
  board: LeaderboardKey;
  rank: number;
  season?: number;
  score?: number;
}

function sameBoard(a: LeaderboardKey, b: LeaderboardKey): boolean {
  return a.family === b.family && a.partition === b.partition;
}

/**
 * Resolve every achievement satisfied by one authoritative leaderboard write.
 * Persistence/idempotency deliberately remain the caller's concern: this pure
 * seam only matches declarative registry conditions and therefore works for a
 * direct jump to rank one (both the broad and narrow milestones are returned).
 */
/** The counters a counter-threshold evaluation reads; absent means 0. */
export type AchievementCounters = Partial<Record<AchievementCounter, number>>;

/**
 * Resolve every counter-threshold achievement satisfied by one set of durable
 * account counters - the pure sibling of {@link leaderboardAchievementsFor}
 * (PRD #669): counters in, satisfied definitions out. A single write that
 * crosses several tiers returns all of them (the 25th duel win satisfies both
 * Duelist and Gladiator); dedup against already-earned grants stays the
 * caller's concern. A missing or non-finite counter satisfies nothing.
 */
export function evaluateCounterAchievements(
  counters: AchievementCounters,
): readonly AchievementDefinition[] {
  return achievements.filter((definition) => {
    if (definition.condition.kind !== 'counter-threshold') return false;
    const value = counters[definition.condition.counter];
    return typeof value === 'number' && Number.isFinite(value) &&
      value >= definition.condition.minimum;
  });
}

export function leaderboardAchievementsFor(
  event: LeaderboardRankEvent,
): readonly AchievementDefinition[] {
  if (!Number.isInteger(event.rank) || event.rank < 1) return [];
  return achievements.filter(
    (definition) =>
      definition.condition.kind === 'leaderboard-rank' &&
      sameBoard(definition.condition.board, event.board) &&
      event.rank <= definition.condition.maximumRank,
  );
}

/** One arena run's boss tally - the only field the slayer matcher reads. */
export interface ArenaBossKillsEvent {
  bossKills: number | null | undefined;
}

/**
 * Resolve every run-boss-kills slayer achievement satisfied by one finished
 * arena run - the {@link leaderboardAchievementsFor} sibling for PRD #669's
 * seven boss slayers: a run with N boss kills returns the first N slayers (the
 * deterministic rotation makes "reached N kills" identical to "defeated the Nth
 * villain"). Registry-driven, so it can never grant more than the seven roster
 * villains and an escalated repeat lap (bossKills >= 8) adds nothing;
 * dedup/persistence stay the caller's concern. A missing/non-finite count
 * satisfies nothing.
 */
export function arenaBossAchievementsFor(
  event: ArenaBossKillsEvent,
): readonly AchievementDefinition[] {
  const bossKills = event.bossKills;
  if (typeof bossKills !== 'number' || !Number.isFinite(bossKills)) return [];
  return achievements.filter(
    (definition) =>
      definition.condition.kind === 'run-boss-kills' &&
      bossKills >= definition.condition.minimumBossKills,
  );
}
